import { createContext, useCallback, useContext, useMemo } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { formatUnicorn } from '@dealstack/shared/lib/format/formatUnicorn.ts';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths.ts';
import { getEntityAudits } from '@dealstack/frontend/shared/dataExchange/services/audit.service.ts';
import { AuditTct } from '@dealstack/shared/lib/generated';
import { transformAuditToTableData } from '@dealstack/frontend/shared/components/Audit/AuditLayout/auditTableData.utils.ts';
import {
	toAuditEntries,
	toEntryRows,
} from '@dealstack/frontend/shared/components/Audit/AuditLayout/auditWrites.utils.ts';
import { CapManageControllerContext } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/CapManageEntityFlow.tsx';
import { getPersonSecurities } from '@dealstack/frontend/shared/dataExchange/services/securities.ts';
import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { useSecurityTypeTranslation } from '@dealstack/frontend/shared/translationControllers/useCommonTranslationsController.ts';

export const AuditContext = createContext<ReturnType<typeof useAuditController>>(
	{} as ReturnType<typeof useAuditController>
);

export const useAuditController = (
	entityId: string | undefined,
	endpointPath?: string,
	securitiesSelectOptionsOverride?: Array<{
		value: string;
		label: string;
		unitDenominator?: number;
	}>
) => {
	const { personsSelectOptions, mipSelectOptions, selectedEntityId } = useContext(
		CapManageControllerContext
	);
	const tCapManage = useTranslationCapManage();
	const tSecurityTypeTranslation = useSecurityTypeTranslation();

	const auditEndpoint =
		endpointPath ??
		formatUnicorn(API_PATHS.AUDIT_ENTITY_ENTITYID_GET, {
			entityId: entityId || '',
		});

	const {
		data: getAuditsRequest,
		refetch: refetchSecurityAudit,
		isLoading,
		isFetching,
	} = useQuery({
		queryKey: [auditEndpoint, { entityId: entityId || '' }],

		queryFn: ({ signal }) => getEntityAudits(auditEndpoint, signal),
		placeholderData: keepPreviousData,
		enabled: !!entityId,
	});

	const getSecuritiesRequest = useQuery({
		queryKey: [
			formatUnicorn(API_PATHS.SECURITY_PERSON_SECURITIES_PERSONID_POST, {
				personId: selectedEntityId,
			}),
		],

		queryFn: ({ signal }) =>
			getPersonSecurities(
				formatUnicorn(API_PATHS.SECURITY_PERSON_SECURITIES_PERSONID_POST, {
					personId: selectedEntityId,
				}),
				undefined,
				signal
			),
		enabled: !!selectedEntityId,
	});

	const internalSecuritiesSelectOptions = useMemo(
		() =>
			getSecuritiesRequest.data?.securities?.map((security) => ({
				value: security.id,
				label: security.name,
				unitDenominator: security.unitDenominator,
			})) || [],
		[getSecuritiesRequest.data]
	);

	const securitiesSelectOptions =
		securitiesSelectOptionsOverride ?? internalSecuritiesSelectOptions;

	// A bank-account save writes the account row and each identifier it touched in one
	// transaction; the history reads that as one entry instead of one per row.
	const entries = useMemo(() => toAuditEntries(getAuditsRequest ?? []), [getAuditsRequest]);
	const entriesById = useMemo(
		() => new Map(entries.map((entry) => [entry.audit.id, entry])),
		[entries]
	);
	const audits = useMemo(() => entries.map((entry) => entry.audit), [entries]);

	const getTransformedAuditData = useCallback(
		(audit: AuditTct | null) => {
			const transform = (row: AuditTct | null) =>
				transformAuditToTableData(row, {
					tCapManage,
					tSecurityTypeTranslation,
					personsSelectOptions,
					mipSelectOptions,
					securitiesSelectOptions,
				});
			const entry = audit ? entriesById.get(audit.id) : undefined;

			return entry ? toEntryRows(entry, transform) : transform(audit);
		},
		[
			entriesById,
			mipSelectOptions,
			personsSelectOptions,
			securitiesSelectOptions,
			tCapManage,
			tSecurityTypeTranslation,
		]
	);

	return {
		audits,
		entityId,
		getAuditsRequest,
		isLoading,
		isFetching,
		getTransformedAuditData,
		refetchSecurityAudit,
	};
};
